import { cn } from '@latitude/token-utils';
import {
  Portal,
  Content as PrimitiveContent,
  Root,
} from '@radix-ui/react-popover';
import type { ComponentPropsWithoutRef, ComponentRef, Ref } from 'react';
import { useRef } from 'react';

import { Trigger } from './components';

const Content = ({
  className,
  align = 'center',
  sideOffset = 4,
  ref,
  // Pulled out of the spread below on purpose. `{...props}` lands last so a
  // consumer can override anything, which would silently replace the focus
  // handlers instead of composing with them.
  onCloseAutoFocus,
  onEscapeKeyDown,
  ...props
}: ComponentPropsWithoutRef<typeof PrimitiveContent> & {
  ref?: Ref<ComponentRef<typeof PrimitiveContent>>;
}) => {
  const dismissedByEscape = useRef(false);

  return (
    <Portal>
      <PrimitiveContent
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        onEscapeKeyDown={(event) => {
          dismissedByEscape.current = true;
          onEscapeKeyDown?.(event);
        }}
        onCloseAutoFocus={(event) => {
          const closedByEscape = dismissedByEscape.current;
          dismissedByEscape.current = false;

          onCloseAutoFocus?.(event);
          if (event.defaultPrevented) {
            return;
          }

          /**
           * Escape is the one close a keyboard user drives, so it is the one
           * that has to hand focus back to the trigger (WCAG 2.4.3). Radix
           * does that itself once this handler leaves the event alone.
           *
           * Every other close keeps focus where it is. A popover's Content
           * unmounts on close, so returning focus on every unmount lets a
           * closing popover pull focus out of a different one that is opening
           * in the same render, and a non-modal popover dismisses itself when
           * focus lands outside it. That is the failure the old blanket
           * `preventDefault()` was hiding: `@latitude/filter-toolbar` hands
           * off from its type picker to the new filter's own popover, and the
           * second popover vanished the moment the first reclaimed focus.
           */
          if (!closedByEscape) {
            event.preventDefault();
          }
        }}
        className={cn(
          [
            'z-[100]',
            'rounded-radius-comp-popover',
            'border-border-ref-medium',
            'border-color-comp-hover-card-border',
            'shadow-box-shadow-sys-medium',
            'bg-color-comp-hover-card-container',
            'py-spacing-comp-popover-vertical',
            'px-spacing-comp-popover-horizontal',
            'animate-in fade-in-0 zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:zoom-out-95 data-[state=closed]:fade-out-0',
            'data-[side=bottom]:slide-in-from-top-2',
            'data-[side=left]:slide-in-from-right-2',
            'data-[side=right]:slide-in-from-left-2',
            'data-[side=top]:slide-in-from-bottom-2',
          ],
          className,
        )}
        {...props}
      />
    </Portal>
  );
};
Content.displayName = PrimitiveContent.displayName;

export { Root, Trigger, Content };
